/**
 * Ripia Auth Module Type Definitions
 *
 * 認証・認可関連の全型定義
 * @module auth/types
 */

import type { UnknownObject } from '../../types/common.types';
import type { DeviceCharacteristics } from '../../utils/deviceAuth';
import type { SalonBusinessType } from '../../constants/businessTypes';

// Re-export SalonBusinessType for backward compatibility
// @see #1346 BusinessType統一化 - SSOT is now @/constants/businessTypes
export type { SalonBusinessType } from '../../constants/businessTypes';

// Re-export from multiTenant.ts for consistency
export type {
  EnhancedJWTPayload,
  TenantInfo,
  UserInfo,
  TenantContext,
  TenantPermissionActions,
  TenantResource,
  PlanFeature,
  MultiTenantApiError,
  ApiRequestConfig,
  MultiTenantLoginRequest,
  MultiTenantLoginResponse,
  TenantSwitchRequest,
  TenantSwitchResponse,
  NotificationSettings,
} from '../../types/multiTenant';

// Runtime value imports (re-export)
export { SecurityErrorType } from '../../types/multiTenant';

// ========================
// Core User Types
// ========================

/**
 * Legacy User Type for backward compatibility
 */
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'staff';
  storeId?: string;
  tenantId?: string;
  permissions?: string[];
  createdAt?: string;
  lastLoginAt?: string;
  // MFA関連プロパティ
  mfaEnabled?: boolean;
  emailVerified?: boolean;
  // Preview環境専用プロパティ
  isPreview?: boolean;
  // Organization management properties
  tenantRole?: 'owner' | 'admin' | 'member' | 'viewer' | 'staff' | 'manager';
}

/**
 * Legacy Types (maintained for backward compatibility)
 */
export type TenantRole = 'owner' | 'manager' | 'staff';
export type PlanType = 'light' | 'standard' | 'premium';

export interface TenantStore {
  id: string;
  name: string;
  code: string;
}

/**
 * Multi-Tenant User (combines legacy User with new TenantContext)
 */
export interface MultiTenantUser extends Omit<User, 'permissions'> {
  tenantContext?: import('../../types/multiTenant').TenantContext;
  availableTenants: import('../../types/multiTenant').TenantInfo[];
  // UserInfo compatibility properties
  tenantRole: import('../../types/multiTenant').EnhancedJWTPayload['tenant_role'];
  isActive: boolean;
  // Override permissions to match UserInfo requirements
  permissions: Record<
    string,
    import('../../types/multiTenant').TenantPermissionActions
  >;
}

// ========================
// JWT Types
// ========================

/**
 * JWT Payload型定義（既存・完全保持 + エンタープライズ拡張）
 */
export interface JWTPayload {
  sub: string; // User ID
  email: string; // ユーザーメール
  role: 'owner' | 'staff'; // 権限
  storeId?: string; // 店舗ID
  permissions: string[]; // 詳細権限
  iat: number; // 発行時刻
  exp: number; // 有効期限
  // エンタープライズセキュリティ拡張
  jti?: string; // JWT ID（無効化管理用）
  device_fp?: string; // デバイスフィンガープリント
  session_id?: string; // セッションID
  security_level?: 'standard' | 'high' | 'premium';
  // MFA（多要素認証）情報
  mfa_enabled?: boolean; // MFAが有効かどうか
  mfa_verified?: boolean; // MFA認証済みかどうか
  mfa_required?: boolean; // MFA認証が必須かどうか
  temp_auth?: boolean; // 一時認証フラグ（MFA前の状態）
}

// ========================
// API Request/Response Types
// ========================

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

// ========================
// MFA Types
// ========================

export interface MFAEnableRequest {
  userId: string;
  secretKey: string;
  verificationCode: string;
  backupCodes: string[];
}

export interface MFAEnableResponse {
  success: boolean;
  message: string;
  backupCodesCount: number;
}

export interface MFADisableRequest {
  userId: string;
  currentPassword: string;
  verificationCode?: string;
  backupCode?: string;
}

export interface MFADisableResponse {
  success: boolean;
  message: string;
}

export interface MFAVerifyRequest {
  tempToken: string;
  code: string;
  isBackupCode?: boolean;
}

export interface MFAVerifyResponse {
  success: boolean;
  message: string;
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  backupCodesRemaining?: number;
}

export interface MFAStatusRequest {
  userId: string;
}

export interface MFAStatusResponse {
  isEnabled: boolean;
  backupCodesRemaining: number;
  lastUsed?: string;
  setupDate?: string;
}

export interface MFAGenerateBackupCodesRequest {
  userId: string;
  currentPassword: string;
  verificationCode?: string;
  backupCode?: string;
}

export interface MFAGenerateBackupCodesResponse {
  success: boolean;
  backupCodes: string[];
  message: string;
}

export interface TempAuthToken {
  tempToken: string;
  expiresAt: number;
  email: string;
  userId: string;
  mfaRequired: boolean;
}

// ========================
// Email Verification Types
// ========================

// SalonBusinessType is imported from '@/constants/businessTypes' (SSOT)
// @see #1346 BusinessType統一化

export interface RegisterWithEmailVerificationRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  // マルチテナント対応フィールド
  shopName?: string;
  businessType?: SalonBusinessType;
  selectedPlan?: PlanType;
}

export interface RegisterWithEmailVerificationResponse {
  success: boolean;
  message: string;
  userId: string;
  verificationRequired: boolean;
  token?: string; // Issue #327 Phase 2: JWT token for Checkout Session API
}

export interface VerifyEmailRequest {
  userId: string;
  verificationCode: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  message: string;
  shouldRedirectToOrganizationCreation: boolean;
  // オプション: メール認証後にトークンが返される場合
  accessToken?: string;
  refreshToken?: string;
  user?: User;
  checkoutUrl?: string; // Issue #327 Phase 2-A: Stripe Checkout URL
}

export interface ResendVerificationEmailRequest {
  userId: string;
}

// Organization Types removed - Clerk Organization dead code cleanup (Phase 3)

// ========================
// Enhanced Security Types
// ========================

export interface EnhancedLoginRequest extends LoginRequest {
  deviceCharacteristics?: DeviceCharacteristics;
  securityLevel?: 'standard' | 'high' | 'premium';
  rememberDevice?: boolean;
}

export interface EnhancedLoginResponse extends LoginResponse {
  jti: string;
  deviceFingerprint?: string;
  securityLevel: 'standard' | 'high' | 'premium';
  isDeviceTrusted: boolean;
}

export interface SecurityAuditInfo {
  tokenId: string;
  issuedAt: number;
  expiresAt: number;
  deviceFingerprint?: string;
  lastActivity: number;
  securityLevel: string;
}

export interface DeviceManagementInfo {
  fingerprint: string;
  trustScore: number;
  lastUsed: number;
  isBlocked: boolean;
  deviceName?: string;
}

// ========================
// Internal Types
// ========================

/**
 * 権限設定型定義
 */
export interface PermissionDetails {
  read: boolean;
  write: boolean;
  delete: boolean;
}

/**
 * マルチテナントデータ型定義
 */
export interface MultiTenantData extends UnknownObject {
  tenantId?: string;
  additionalInfo?: UnknownObject;
}

/**
 * メンバー情報型定義
 */
export interface MemberData {
  userId: string;
  newRole: string;
  updatedAt: string;
}

/**
 * API レスポンス型定義
 */
export interface ApiResponseData {
  success: boolean;
  message?: string;
  data?: UnknownObject;
  invitationToken?: string;
  expiresAt?: string | Date;
  tenantContext?: import('../../types/multiTenant').TenantContext;
}

// ========================
// Error Types
// ========================

/**
 * Auth Error Codes
 */
export type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'TOKEN_EXPIRED'
  | 'NETWORK_ERROR'
  | 'UNAUTHORIZED'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'TOKEN_REFRESHED'
  | 'SESSION_EXPIRED'
  | 'AUTHENTICATION_FAILED'
  | 'AUTHORIZATION_FAILED'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMIT_EXCEEDED'
  | 'SERVICE_UNAVAILABLE'
  | 'REGISTRATION_FAILED'
  | 'VERIFICATION_FAILED'
  | 'RESEND_FAILED'
  | 'VERIFICATION_NOT_FOUND'
  | 'VERIFICATION_EXPIRED'
  | 'INVALID_VERIFICATION_CODE'
  | 'AUTHENTICATION_REQUIRED'
  | 'ORGANIZATION_CREATION_FAILED'
  | 'DEFAULT_TENANT_CREATION_FAILED'
  | 'ORGANIZATION_NOT_FOUND'
  | 'ORGANIZATION_ACCESS_DENIED'
  | 'CONTEXT_SETTING_FAILED'
  | 'MFA_ENABLE_FAILED'
  | 'MFA_DISABLE_FAILED'
  | 'MFA_VERIFICATION_FAILED'
  | 'MFA_STATUS_FAILED'
  | 'BACKUP_CODES_GENERATION_FAILED'
  | 'TEMP_TOKEN_EXPIRED'
  | 'INVALID_MFA_CODE'
  | 'POPUP_BLOCKED'
  | 'AUTH_ERROR'
  | 'TIMEOUT';

/**
 * Auth Error Class
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public code: AuthErrorCode,
    public status?: number
  ) {
    super(message);
    this.name = 'AuthError';
  }
}
